import { FC, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { Web3ReactProvider } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";
import { Box, Center, Flex, Spinner, ChakraProvider } from "@chakra-ui/react";
import { Dict } from "@chakra-ui/utils";
import _keys from "lodash/keys";
import { useCookies } from "react-cookie";
import { Userpilot } from "userpilot";

import { AppProps } from "next/app";
import Head from "next/head";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// Global CSS
require("../styles/globals.css");

// Providers
import { StakeProvider } from "providers/StakeProvider";

// Hooks
import useLocalStorage from "hooks/useLocalStorage";
import useMounted from "hooks/useMounted";

// Components
import Sidebar from "components/Sidebar";
import Banner from "components/Banner";
import HandleThemeChange from "components/HandleThemeChange";

// Contants
import {
  menuItems,
  extraItems,
  liquidStakingOptions,
} from "constants/sidebarMenus";
import { NETWORK_KEY } from "constants/constants";

// Firebase Utils
import "utils/firebase";

// Config
import { ethConfig, polygonConfig } from "config/network";

// Types
import { IIndexable } from "types/common";

import { ApolloProvider } from "@apollo/client";
import { balancerClient } from "apollo-client";
import { trackEvent } from "utils/firebase";
import Script from "next/script";

import { DefaultScripts } from "app-head";

// Services
import { getBannerData } from "services/defi";

function getLibrary(provider: any) {
  const library = new Web3Provider(provider);
  library.pollingInterval = 5000;
  return library;
}

const DefaultHead = () => {
  return (
    <Head>
      <title>Stader | Polygon</title>
      <meta name="description" content="Stake Matic with Stader" />
      <link rel="icon" href="/favicon.ico" />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <meta
        name="og:description"
        content="Stader's Matic liquid staking gives you a liquid token that gives you max rewards, instant liquidity, and the ability to participate in Defi."
      />
      <meta name="og:title" content="Matic Liquid Staking" />
      <meta />
      <DefaultScripts />
    </Head>
  );
};

const chainMapping: IIndexable = {
  "0x1": "ETHEREUM",
  "0x89": "POLYGON",
};

const initialState = {
  text: null,
  link: null,
  show: false,
  ctaText: null,
};

const cookieName = "promo_banner_disabled";

Userpilot.initialize("NX-87ff200f");

const App: FC<AppProps> = ({ Component, pageProps }) => {
  const [bannerDetails, setBannerDetails] = useState(initialState);

  const [cookies, setCookie] = useCookies([cookieName]);

  const [selectedChain, setSelectedChain] = useLocalStorage(
    NETWORK_KEY,
    "0x89"
  );

  const [theme, setTheme] = useState<Dict>();
  const [routerLoading, setRouterLoading] = useState(false);
  const isMounted = useMounted();

  const closeBanner = () => {
    setCookie(cookieName, true, {
      maxAge: 1209600,
    });
    setBannerDetails((prev) => ({ ...prev, show: false }));
  };

  const router = useRouter();

  const isLandingPage = (Component as any).isLandingPage as boolean;

  useEffect(() => {
    (async function getBannerDetails() {
      if (cookies.promo_banner_disabled) {
        return;
      }

      const response = await getBannerData();

      if (response.error) {
        return;
      }

      setBannerDetails(response);
    })();
  }, [isMounted]);

  useEffect(() => {
    const handleStart = (url: string) =>
      url !== router.asPath && setRouterLoading(true);
    const handleComplete = (url: string) => {
      if (url === router.asPath) {
        Userpilot.reload();
        setRouterLoading(false);
        trackEvent("page_view", {
          page_location: url,
          page_title: document?.title,
        });
      }
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  });

  useEffect(() => {
    if (!_keys(chainMapping).includes(selectedChain)) {
      setSelectedChain("0x89");
    }
  }, []);

  const toggleChain = () => {
    setSelectedChain((prev: string) => (prev === "0x89" ? "0x1" : "0x89"));
  };

  const config = selectedChain === "0x89" ? polygonConfig : ethConfig;

  const chainProps = {
    selectedChain,
    toggleChain,
    config,
    isPolygonChainActive: selectedChain === "0x89",
    setSelectedChain: (ch: string | number) => setSelectedChain(ch),
  };

  if (!config) {
    return null;
  }

  return (
    <>
      <ChakraProvider theme={theme}>
        <HandleThemeChange onChange={setTheme} />
        <DefaultHead />
        <Web3ReactProvider getLibrary={getLibrary}>
          <ApolloProvider client={balancerClient}>
            {!isLandingPage ? (
              <StakeProvider chainDetails={chainProps}>
                {bannerDetails.show && bannerDetails.text && (
                  <Banner
                    text={bannerDetails.text}
                    link={bannerDetails.link || ""}
                    ctaText={bannerDetails.ctaText || "Learn More"}
                    closeBanner={closeBanner}
                  />
                )}
                <Sidebar
                  menuItems={menuItems}
                  extraItems={extraItems}
                  stakingOptions={liquidStakingOptions}
                  minH={{
                    base: "100vh",
                    xl: bannerDetails.show ? "calc(100vh - 48px)" : "100vh",
                  }}
                >
                  <Center>
                    <Box w="1280px">
                      {routerLoading ? (
                        <Flex
                          alignItems="center"
                          justifyContent="center"
                          h="500px"
                        >
                          <Spinner size="xl" color="brand.primary" />
                        </Flex>
                      ) : (
                        <Component {...pageProps} />
                      )}
                    </Box>
                  </Center>
                </Sidebar>
              </StakeProvider>
            ) : (
              <Component {...pageProps} />
            )}
          </ApolloProvider>
        </Web3ReactProvider>
      </ChakraProvider>
      <Script
        id="chatbot"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            var _smartsupp = _smartsupp || {};
            _smartsupp.key = 'fc7a0f089927a2b699de7d1445aa5d3b7ddf5498';
            _smartsupp.color = '#7630EC';
            window.smartsupp||(function(d) {
              var s,c,o=smartsupp=function(){ o._.push(arguments)};o._=[];
              s=d.getElementsByTagName('script')[0];c=d.createElement('script');
              c.type='text/javascript';c.charset='utf-8';c.async=true;
              c.src='https://www.smartsuppchat.com/loader.js?';s.parentNode.insertBefore(c,s);
            })(document);
          `,
        }}
      />
    </>
  );
};

export default App;
